(ns graph-ui.web.prompt-area
  "Rich prompt input widget for the UI-DSL (F-graph-ui-v0-003-01-prompt-area).
   Implements a subset of the prompt-area feature spec: multi-line editable
   buffer, optional live markdown preview, optional auto-grow, and :on-submit
   on Ctrl/Cmd+Enter. Web renderer only — the TUI falls back to plain text.

   This namespace is intentionally self-contained: it does not depend on the
   uncommitted graph-ui.web.widgets or graph-ui.web.markdown, so adding it does
   not touch the other worker's in-flight files."
  (:require [reagent.core :as r]
            [clojure.string :as str]
            [graph-ui.web.bind :as bind]
            [graph-ui.web.voice :as voice]))

;; @feature F-graph-ui-v0-003-01-prompt-area
;; @adr ADR-graph-ui-v0-004-01-meta-ui-versioning
;; @adr ADR-graph-ui-v0-007-01-radix-web-primitives

;; ---------------------------------------------------------------------
;; Design tokens — kept local so this new namespace is independent of the
;; uncommitted widgets.cljs. The values mirror the v1.1 token tables there
;; so the widget looks consistent with :text-area.
;; ---------------------------------------------------------------------

(def ^:private tone-text-classes
  {:muted   "ikc-fg-faint"
   :accent  "ikc-fg-accent"
   :success "ikc-fg-good"
   :warning "ikc-fg-warn"
   :danger  "ikc-fg-crit"})

(def ^:private input-state-classes
  {:invalid "ikc-bd-crit"})

(def ^:private box-density-pad-classes
  {:compact "p-1" :roomy "p-4"})

;; ---------------------------------------------------------------------
;; Minimal markdown preview — self-contained, does not depend on the
;; uncommitted graph-ui.web.markdown. Handles **bold**, *italic*, and
;; `inline code` across paragraphs. Nested/overlapping styles are not
;; supported in v1.
;; ---------------------------------------------------------------------

(defn- md-preview-pieces
  "Split `s` into a sequence of hiccup pieces: plain :span, :strong, :em,
   or :code. Empty strings are dropped."
  [s]
  (loop [s (str s) acc []]
    (if (empty? s)
      acc
      (let [re (js/RegExp. "^(.*?)((\\*\\*)([^*]+)(\\*\\*)|(\\*)([^*]+)(\\*)|(`)([^`]+)(`))" "")
            m  (.exec re s)]
        (if m
          (let [before  (aget m 1)
                full    (aget m 2)
                content (or (aget m 4) (aget m 7) (aget m 10))
                tag     (cond (aget m 3) :strong
                              (aget m 6) :em
                              (aget m 9) :code)]
            (recur (subs s (+ (count before) (count full)))
                   (cond-> acc
                     (seq before) (conj [:span before])
                     true         (conj [tag content]))))
          (conj acc [:span s]))))))

(defn- md-preview
  "Render a simple markdown preview of `text`: paragraphs split on blank
   lines, inline styles applied inside each paragraph."
  [text]
  (let [paragraphs (str/split (str text) #"\n\s*\n+")]
    (into [:div {:class "text-sm space-y-1 overflow-auto"}]
      (for [p paragraphs]
        (into [:p {:class "m-0"}] (md-preview-pieces p))))))

;; ---------------------------------------------------------------------
;; Auto-grow
;; ---------------------------------------------------------------------

(defn- auto-grow!
  "Set a textarea's height to its scrollHeight so it grows with content.
   Call after the DOM updates for a non-zero scrollHeight."
  [el]
  (when el
    (set! (.. el -style -height) "auto")
    (set! (.. el -style -height) (str (.-scrollHeight el) "px"))))

;; ---------------------------------------------------------------------
;; Component
;; ---------------------------------------------------------------------

(defn refusal-text
  "The reason of a refused send, as one sentence, or nil for an accepted one.
   A refusal is a map with :error. Its :note is the sentence when the handler
   wrote one. Test: T-web-prompt-area-013."
  [result]
  (when (and (map? result) (:error result))
    (str "Not sent. "
         (or (not-empty (str (:note result)))
             (str "The bus refused it: " (name (:error result)) ".")))))

(defn submit!
  "Dispatch :on-submit filled with the current buffer and session-id.

   THE ANSWER IS READ, NOT DROPPED (IKC3/PLAN-ikc-v3-060-01/FR-S). George,
   2026-10-06: \"Also cannot make its \"Ask gi\" to make a change or answer a
   question.\" :ui/send-to-counterpart answered :no-counterpart with a note,
   and this callback was (fn [_] nil), so nothing on the screen said why. Now
   the reason goes into `refusal`, which hint-line draws. The words stay in
   the buffer, so the person sends them again after the fix.

   AN ACCEPTED SEND EMPTIES THE BOX. The words were in it after an answer
   too, so a question looked unsent. Only an accepted reply empties it, so a
   refused or lost send keeps the words (memory stall-never-vanish).
   Test: T-web-prompt-area-013."
  ([on-submit bus session-id buf] (submit! on-submit bus session-id buf nil))
  ([on-submit bus session-id buf refusal]
   (when (and on-submit bus)
     (js-invoke bus "dispatch"
       (bind/fill-command on-submit {'$session-id session-id '$value @buf})
       (fn [result]
         (let [reason (refusal-text result)]
           (when refusal (reset! refusal reason))
           (when (and refusal (nil? reason)) (reset! buf ""))))))))

(defn hint-line
  "The text in the row of the Send button: the key hint, or the reason of the
   last refused send. ONE SLOT, so a reason that comes and goes moves nothing
   (memory an-indicator-must-not-move-the-furniture)."
  [reason]
  (if reason
    [:span {:class "text-xs ikc-fg-warn" :role "alert" :data-refusal "true"} reason]
    [:span {:class "text-xs text-[var(--faint)]"} "Enter to send · Shift+Enter for a new line"]))

(defn- image-type?
  [t]
  (str/starts-with? (str t) "image/"))

(defn- seq-of
  "The entries of a DataTransferItemList or a FileList, or ()."
  [xs]
  (if xs (map #(aget xs %) (range (.-length xs))) ()))

(defn first-image
  "The first image file of a FileList, or nil. A picked file and a dropped
   one are read the same way. Test: T-web-prompt-area-011."
  [files]
  (some (fn [f] (when (image-type? (.-type f)) f)) (seq-of files)))

(defn image-file-of
  "The first image file an event carries, or nil.

   THE FILE'S OWN TYPE ANSWERS THE ONE QUESTION this side has to answer: is
   there an image here? A paste of text carries no file at all, and a picture
   copied from a browser arrives with several flavours beside it. The BYTES are
   parsed on the server (ui.attachments/parse-data-url), where a test can reach
   the parse.

   EVERY SHAPE THAT CARRIES AN IMAGE, NOT ONLY ONE (fault report 08e4f4c1,
   2026-10-04: \"cannot paste image on Samsung s26 Ultra\"). This read only
   clipboardData.items of a paste. It now reads:

   - clipboardData of a paste, and dataTransfer of a drop
   - the .items list, and then the .files list. An engine can put the file in
     one list only.

   Tests: T-web-prompt-area-006..009."
  [e]
  (some (fn [carrier]
          (or (some (fn [it]
                      (when (and (= "file" (.-kind it)) (image-type? (.-type it)))
                        (.getAsFile it)))
                    (seq-of (.-items carrier)))
              (first-image (.-files carrier))))
        (remove nil? [(.-clipboardData e) (.-dataTransfer e)])))

(defn pick-image!
  "Open the browser's file picker for one image, and call `on-file` with it.

   THE PATH THAT WORKS IN EVERY BROWSER (fault report 08e4f4c1). Firefox for
   Android gives a keyboard image or a pasted image only to a contenteditable
   field (Bug 1714496, Bug 1839582), never to a textarea. A file input opens
   the photo picker or the camera there, and in Chrome and on a desktop too.

   The input is put in the page while it is open, because a browser may
   refuse a click on an input that is not in the document."
  [on-file]
  (when (exists? js/document)
    (let [inp (.createElement js/document "input")]
      (set! (.-type inp) "file")
      (set! (.-accept inp) "image/*")
      (set! (.. inp -style -display) "none")
      (.addEventListener inp "change"
                         (fn [_]
                           (when-let [f (first-image (.-files inp))] (on-file f))
                           (.remove inp)))
      (.appendChild (.-body js/document) inp)
      (.click inp))))

(defn pick-file!
  "Open the browser's file picker for one file of ANY type, and call `on-file`
   with it. The same input as pick-image!, with no `accept` filter
   (PLAN-ikc-v3-061-01, \"Insert file\")."
  [on-file]
  (when (exists? js/document)
    (let [inp (.createElement js/document "input")]
      (set! (.-type inp) "file")
      (set! (.. inp -style -display) "none")
      (.addEventListener inp "change"
                         (fn [_]
                           (when-let [f (some-> (.-files inp) (aget 0))] (on-file f))
                           (.remove inp)))
      (.appendChild (.-body js/document) inp)
      (.click inp))))

(defn carries-files?
  "True when a drag carries files. Only then does a field take the drop, so a
   drag of text keeps the browser's own behaviour."
  [e]
  (boolean (some #{"Files"} (seq-of (some-> e .-dataTransfer .-types)))))

(defn fire-paste!
  "Read one pasted image and fire the :paste gesture with the whole `data:` URL.

   THE GESTURE AND NOT A DISPATCH OF OUR OWN. A tile bakes its own session into
   every gesture it holds (ADR-graph-ui-v0-023-01), so the template already
   names the right session. A paste that dispatched :ui/attach-image itself
   attached the image to the TAB'S session — measured in the browser on
   2026-08-23: the file was written and the tile showed nothing.

   NOTHING ABOUT THE IMAGE ENTERS THE BUFFER, so the words a person typed are
   untouched."
  [on-paste bus session-id file]
  (when (and on-paste bus file)
    (let [rdr (js/FileReader.)]
      (set! (.-onload rdr)
            (fn [_]
              (js-invoke bus "dispatch"
                         (bind/fill-command on-paste {'$session-id session-id
                                                      '$value (.-result rdr)})
                         (fn [_] nil))))
      (.readAsDataURL rdr file))))

(defn widget-prompt-area
  "Rich multi-line prompt input. Local buffer seeded from :value at mount.
   On Ctrl/Cmd+Enter dispatches :on-submit with $value = buffer. Optional
   :auto-grow expands the textarea as the user types; :markdown toggles a
   live preview below the textarea.

   IT LENDS ITS BUFFER TO THE VOICE SURFACE (PLAN-ikc-v3-015-01 phase 6). The
   buffer stays this component's own state — nothing reaches in — so instead
   this widget REGISTERS the atom under its :import-id while it is mounted, and
   a :voice node whose :target names that id appends what it heard. The reader
   then reads it, corrects it and sends it. Dictation never dispatches, which is
   the standing rule that user input travels verbatim and a person decides.

   Registered from the textarea's own ref, so the registration lasts exactly as
   long as the component: React calls a ref with nil on unmount, which is the
   deregistration and needs no lifecycle class."
  [{:keys [value] :as _initial-props} _bus _session-id]
  (let [buf      (r/atom (str (or value "")))
        textarea (r/atom nil)
        refusal  (r/atom nil)]
    (fn [{:keys [placeholder rows auto-grow markdown state tone density
                 import-id] :as props} bus session-id]
      ;; The template comes from bind/submit-template, which reads BOTH
      ;; spellings. Reading :on-submit alone left every v2.0 description
      ;; with a dead Send button (2026-08-20).
      (let [on-submit (bind/submit-template props)
            paste-tmpl (bind/gesture-template props :paste)
            disabled? (= state :disabled)
            pad-class (get box-density-pad-classes density "p-2")
            grow!     #(when (and auto-grow @textarea) (auto-grow! @textarea))]
        [:div {:class (str "flex flex-col flex-1 min-h-0 gap-1 " pad-class)}
         [:textarea
          {:value       @buf
           :placeholder (or placeholder "")
           :rows        (or rows 4)
           :disabled    disabled?
           :ref         (fn [el]
                          (if el
                            (do (reset! textarea el)
                                (voice/register-prompt! import-id buf)
                                (js/setTimeout grow! 0))
                            (voice/forget-prompt! import-id)))
           :class       (str "flex-1 min-h-0 w-full resize-none font-sans text-sm rounded-md px-2 py-1 "
                              "bg-[var(--field)] border "
                              (get input-state-classes state "border-[var(--border)]") " "
                              (get tone-text-classes tone "text-[var(--text)]")
                              " focus:outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-soft)]"
                              (when disabled? " ikc-dim cursor-not-allowed"))
           ;; The reason belongs to the last send. A person who comes back to
           ;; the box, after a pick in the picker, is about to send again.
           :on-focus    #(reset! refusal nil)
           :on-change   (fn [e]
                          (reset! buf (.. e -target -value))
                          ;; A reason is about the words that were sent. New
                          ;; words have not been refused yet.
                          (reset! refusal nil)
                          (js/setTimeout grow! 0))
           ;; ENTER SENDS, Shift+Enter makes a newline (the declaration in
           ;; ui.edn says so, 2026-08-20). Ctrl/Cmd+Enter keeps working for
           ;; the hands that learned it.
           :on-key-down (fn [e]
                          (when (and (= 13 (.-keyCode e))
                                     (not (.-shiftKey e)))
                            (.preventDefault e)
                            (submit! on-submit bus session-id buf refusal)))
           ;; A PASTED IMAGE GOES WITH THE PROMPT (PLAN-ikc-v3-017-01 phase 11).
           ;; George, 2026-08-21: "I often paste an image in the terminal — is it
           ;; supported in a tile?"
           ;;
           ;; ONLY WHEN THE DESCRIPTION ASKS FOR IT, and only for an image. A
           ;; node with no :paste gesture keeps the browser's own paste, and a
           ;; paste of text is untouched everywhere.
           ;;
           ;; A DROPPED IMAGE TAKES THE SAME PATH, and data-image-input marks the
           ;; box for the stylesheet. Under a finger that makes it a field an
           ;; Android keyboard may put an image into (fault report 08e4f4c1).
           :data-image-input (when paste-tmpl "true")
           :on-paste    (fn [e]
                          (when-let [f (and paste-tmpl (image-file-of e))]
                            (.preventDefault e)
                            (fire-paste! paste-tmpl bus session-id f)))
           :on-drag-over (fn [e]
                           (when (and paste-tmpl (carries-files? e))
                             (.preventDefault e)))
           :on-drop     (fn [e]
                          (when-let [f (and paste-tmpl (image-file-of e))]
                            (.preventDefault e)
                            (fire-paste! paste-tmpl bus session-id f)))}]
         (when markdown
           [:div {:class "flex-1 min-h-0 overflow-auto border-t border-[var(--border)] pt-1"}
            [md-preview @buf]])
         (when (and on-submit (not disabled?))
           [:div {:class "flex flex-row items-center justify-end gap-2"}
            [hint-line @refusal]
            [:button {:class "text-xs px-3 py-1 rounded-md font-semibold bg-[var(--accent)] text-[var(--field)] ikc-hover"
                      :on-click #(submit! on-submit bus session-id buf refusal)}
             "Send"]])]))))
